'use client'

import { CircleHelp } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useOptionTableConfig } from '@/features/chart/hooks/use-option-table-config'
import {
  OPTION_COLUMN_GROUPS,
  OPTION_TABLE_COLUMNS,
  OptionTableColumn,
} from '@/features/chart/lib/configs/option-setting-config'
import { cn } from '@/lib/utils'

export default function OptionColumnsTab({ className }: { className?: string }) {
  const t = useTranslations('trade.optionColumns')
  const { columns, updateColumn } = useOptionTableConfig()

  return (
    <div className={cn('flex max-h-100 w-64 flex-col gap-3 overflow-y-auto p-4', className)}>
      {Object.entries(OPTION_COLUMN_GROUPS).map(([groupKey, group]) => (
        <div key={groupKey} className="flex flex-col gap-1">
          {/* Group Header */}
          <div className="text-foreground/60 mb-1 text-xs font-medium tracking-tight">
            {t(`groups.${groupKey}`)}
          </div>
          {/* Group Items */}
          <div className="flex flex-col gap-0.5">
            {group.columns.map((columnKey) => {
              const meta = OPTION_TABLE_COLUMNS[columnKey]
              const isChecked = columns[columnKey]
              const isDisabled = columnKey === 'bid' || columnKey === 'ask'

              return (
                <div
                  className={cn([
                    'flex items-center justify-between py-1 pl-1',
                    isDisabled && 'opacity-50',
                  ])}
                  key={columnKey}
                >
                  <div className="flex items-center gap-1">
                    <label
                      className={cn('text-sm tracking-tight', !isDisabled && 'cursor-pointer')}
                      onClick={() => {
                        if (isDisabled) return
                        updateColumn(columnKey as keyof OptionTableColumn, !isChecked)
                      }}
                    >
                      {t(`columns.${columnKey}.name`)}
                    </label>
                    {meta.description && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="text-foreground/30 hover:text-foreground/60 inline-flex cursor-pointer transition-colors">
                            <CircleHelp className="size-3" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent side="right" className="w-auto max-w-55 p-2 text-xs">
                          {t(`columns.${columnKey}.description`)}
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                  <Checkbox
                    className="disabled:data-[state=checked]:bg-foreground/40 disabled:data-[state=checked]:border-transparent"
                    checked={isChecked}
                    onCheckedChange={(checked) => {
                      if (typeof checked === 'boolean') {
                        updateColumn(columnKey as keyof OptionTableColumn, checked)
                      }
                    }}
                    disabled={isDisabled}
                  />
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
